/**
 * Keyboard Configuration Types and Storage
 * 
 * Handles customization of AAC Keyboard toolbar and action row visibility/order
 */

export interface KeyboardToolbarItem {
  id: string;
  labelHe: string;
  labelEn: string;
  iconName: string;
  enabled: boolean;
  order: number;
}

export interface KeyboardActionItem {
  id: string;
  labelHe: string;
  labelEn: string;
  iconName: string;
  enabled: boolean;
  order: number;
}

export interface KeyboardConfig {
  toolbarItems: KeyboardToolbarItem[];
  actionItems: KeyboardActionItem[];
  updatedAt: string;
}

// Default toolbar items (bottom of keyboard)
// Order matches actual keyboard layout (RTL): Language, Emoji, Numbers, Symbols
const DEFAULT_TOOLBAR_ITEMS: KeyboardToolbarItem[] = [
  { id: "language", labelHe: "שפה/אותיות", labelEn: "Language", iconName: "Globe", enabled: true, order: 0 },
  { id: "emoji", labelHe: "אימוג׳י", labelEn: "Emoji", iconName: "Smile", enabled: true, order: 1 },
  { id: "numbers", labelHe: "מספרים", labelEn: "Numbers", iconName: "Hash", enabled: true, order: 2 },
  { id: "symbols", labelHe: "סמלים", labelEn: "Symbols", iconName: "AtSign", enabled: true, order: 3 },
];

// Default action items (top action row) — mirrors Communication Board order
// but with "Communication Board" instead of "Keyboard" for pageToggle
const DEFAULT_ACTION_ITEMS: KeyboardActionItem[] = [
  { id: "deleteWord", labelHe: "מחק מילה", labelEn: "Delete Word", iconName: "Delete", enabled: true, order: 0 },
  { id: "speak", labelHe: "דיבור", labelEn: "Speak", iconName: "Volume2", enabled: true, order: 1 },
  { id: "clear", labelHe: "נקה", labelEn: "Clear", iconName: "Trash2", enabled: true, order: 2 },
  { id: "pageToggle", labelHe: "לוח תקשורת", labelEn: "Communication Board", iconName: "Keyboard", enabled: true, order: 3 },
  { id: "send", labelHe: "שליחה", labelEn: "Send Message", iconName: "Mail", enabled: true, order: 4 },
];

const STORAGE_KEY = "keyboardConfig_v1";

// Load keyboard config from localStorage
export function loadKeyboardConfig(): KeyboardConfig {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      let config: KeyboardConfig = JSON.parse(saved);
      
      // Migration: Remove deprecated items and ensure new defaults exist
      const deprecatedIds = ['acronym', 'phrases', 'deleteChar'];
      const hasDeprecated = config.toolbarItems.some(item => item.id === 'acronym' || item.id === 'phrases');
      const hasDeprecatedAction = config.actionItems.some(item => deprecatedIds.includes(item.id));
      
      if (hasDeprecated || hasDeprecatedAction) {
        // Clean toolbar
        config = {
          ...config,
          toolbarItems: config.toolbarItems
            .filter(item => item.id !== 'acronym' && item.id !== 'phrases')
            .map((item, idx) => ({ ...item, order: idx })),
          actionItems: config.actionItems
            .filter(item => !deprecatedIds.includes(item.id))
            .map((item, idx) => ({ ...item, order: idx })),
          updatedAt: new Date().toISOString(),
        };
      }
      
      // Ensure all default action items exist (forward compat)
      const existingActionIds = config.actionItems.map(i => i.id);
      const missingActions = DEFAULT_ACTION_ITEMS.filter(d => !existingActionIds.includes(d.id));
      if (missingActions.length > 0) {
        const maxOrder = Math.max(...config.actionItems.map(i => i.order), -1);
        config.actionItems = [
          ...config.actionItems,
          ...missingActions.map((item, idx) => ({ ...item, order: maxOrder + 1 + idx })),
        ];
      }
      
      if (hasDeprecated || hasDeprecatedAction || missingActions.length > 0) {
        saveKeyboardConfig(config);
      }
      
      return config;
    }
  } catch (e) {
    console.error("Failed to load keyboard config:", e);
  }
  
  // Return default
  return {
    toolbarItems: [...DEFAULT_TOOLBAR_ITEMS],
    actionItems: [...DEFAULT_ACTION_ITEMS],
    updatedAt: new Date().toISOString(),
  };
}

// Save keyboard config to localStorage
export function saveKeyboardConfig(config: KeyboardConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.error("Failed to save keyboard config:", e);
  }
}

// Update toolbar item
export function updateToolbarItem(
  config: KeyboardConfig,
  itemId: string,
  updates: Partial<Omit<KeyboardToolbarItem, 'id'>>
): KeyboardConfig {
  return {
    ...config,
    toolbarItems: config.toolbarItems.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    ),
    updatedAt: new Date().toISOString(),
  };
}

// Update action item
export function updateActionItem(
  config: KeyboardConfig,
  itemId: string,
  updates: Partial<Omit<KeyboardActionItem, 'id'>>
): KeyboardConfig {
  return {
    ...config,
    actionItems: config.actionItems.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    ),
    updatedAt: new Date().toISOString(),
  };
}

// Reorder toolbar items
export function reorderToolbarItems(
  config: KeyboardConfig,
  fromIndex: number,
  toIndex: number
): KeyboardConfig {
  const newItems = [...config.toolbarItems];
  const [moved] = newItems.splice(fromIndex, 1);
  newItems.splice(toIndex, 0, moved);
  
  // Update order property
  newItems.forEach((item, idx) => {
    item.order = idx;
  });
  
  return {
    ...config,
    toolbarItems: newItems,
    updatedAt: new Date().toISOString(),
  };
}

// Reorder action items
export function reorderActionItems(
  config: KeyboardConfig,
  fromIndex: number,
  toIndex: number
): KeyboardConfig {
  const newItems = [...config.actionItems];
  const [moved] = newItems.splice(fromIndex, 1);
  newItems.splice(toIndex, 0, moved);
  
  // Update order property
  newItems.forEach((item, idx) => {
    item.order = idx;
  });
  
  return {
    ...config,
    actionItems: newItems,
    updatedAt: new Date().toISOString(),
  };
}

// Reset to defaults
export function resetKeyboardConfig(): KeyboardConfig {
  const config: KeyboardConfig = {
    toolbarItems: [...DEFAULT_TOOLBAR_ITEMS],
    actionItems: [...DEFAULT_ACTION_ITEMS],
    updatedAt: new Date().toISOString(),
  };
  saveKeyboardConfig(config);
  return config;
}

// Get default items for reference
export function getDefaultToolbarItems(): KeyboardToolbarItem[] {
  return DEFAULT_TOOLBAR_ITEMS;
}

export function getDefaultActionItems(): KeyboardActionItem[] {
  return DEFAULT_ACTION_ITEMS;
}
